import { useState, useEffect, useCallback } from 'react';
import type { ThemeMode } from '@/lib/theme';

/*
 * Day or night for every page outside the web app, by the web app's own rule
 * (Blake, 2026-09-14): day from 6:30 AM through 6:29 PM local time, night
 * otherwise, and a choice the visitor pins wins over the clock.
 *
 * The rule and the storage key are the web's, byte for byte: public/web/data.js
 * `store.mode` reads localStorage "gbz_theme_override" and otherwise returns
 * day for minutes 390 through 1109. Sharing both means a visitor who pins night
 * on the web sees night on Help and About too, and the two never disagree at
 * 6:30.
 *
 * This replaces a sunrise and sunset calculation that asked for the visitor's
 * location and, when refused, sent their IP address to ip-api.com. The web app
 * never did either, and the two sites switched at different moments.
 */

const STORAGE_KEY = 'gbz_theme_override';
const POLL_INTERVAL = 60 * 1000;

export function clockMode(now: Date = new Date()): ThemeMode {
  const mins = now.getHours() * 60 + now.getMinutes();
  return mins >= 390 && mins <= 1109 ? 'day' : 'night';
}

function readOverride(): ThemeMode | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'day' || v === 'night' ? v : null;
  } catch {
    return null;
  }
}

export function useDayNightMode() {
  const [override, setOverride] = useState<ThemeMode | null>(readOverride);
  const [clock, setClock] = useState<ThemeMode>(() => clockMode());

  useEffect(() => {
    const tick = () => {
      setClock(clockMode());
      // The web app can pin or clear the choice in another tab.
      setOverride(readOverride());
    };
    const id = setInterval(tick, POLL_INTERVAL);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const mode = override ?? clock;

  const toggle = useCallback(() => {
    const next: ThemeMode = mode === 'day' ? 'night' : 'day';
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
    setOverride(next);
  }, [mode]);

  const resetOverride = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setOverride(null);
    setClock(clockMode());
  }, []);

  return { mode, toggle, resetOverride, hasOverride: override !== null };
}
